import React, { useCallback, useContext, useMemo } from 'react';
import { Form, FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';
import SessionContext from '@providers/Session/SessionContext';
import ActionTypes from '@utils/deviceActions';
import EditableTable from '@components/EditableTable';
import { IDevice } from '@pages/Assets/Inventory/types';
import {
  DeviceActionProps,
  DeviceActionStepsProps,
} from '@components/Actions/ActionsModal/types';

interface SimcardChangeProps {
  devices: IDevice[];
  form: FormInstance<any>;
  isUpload?: boolean;
}

const useSimcardChange = ({
  devices,
  form,
  isUpload,
}: SimcardChangeProps): DeviceActionProps => {
  const { t } = useTranslation([
    'actions-modal',
    'device-actions',
    'attributes',
  ]);
  const { isMot } = useContext(SessionContext);

  const changedDevices = Form.useWatch('changedDevices', form);

  const treatedDevices = useMemo(
    () =>
      devices?.map(device => ({
        key: device.id,
        iccid: device.iccid,
        processData: undefined,
        idContract: device.contractAssetNumber,
      })),
    [devices],
  );

  const handleDevicesChange = useCallback(
    (items: any[]) => form?.setFieldValue('changedDevices', items),
    [form],
  );

  const confirmDisabled = useMemo(() => {
    const rows = changedDevices ?? treatedDevices ?? [];
    return rows.some(
      (device: any) =>
        device.processData && device.processData === device.iccid,
    );
  }, [changedDevices, treatedDevices]);

  const steps: DeviceActionStepsProps[] = useMemo(() => {
    if (isUpload) {
      return [];
    }

    return [
      {
        title: t('actions-modal:exchange', { type: 'ICCID' }),
        name: 'changedDevices',
        initialValue: treatedDevices,
        content: (
          <EditableTable
            data={changedDevices ?? treatedDevices}
            columns={[
              {
                title: 'ICCID',
                key: 'iccid',
                dataIndex: 'iccid',
                width: 220,
              },
              {
                title: t('actions-modal:exchange', { type: 'ICCID' }),
                key: 'processData',
                dataIndex: 'processData',
                editable: true,
                isRequired: true,
                width: 220,
              },
            ]}
            allowDelete={false}
            onChange={handleDevicesChange}
          />
        ),
      },
    ];
  }, [isUpload, t, treatedDevices, changedDevices, handleDevicesChange]);

  const payloadToSend = useMemo(() => {
    const devicesPayload = isUpload
      ? devices?.map(device => ({
          iccid: device.iccid,
          processData: device.processData,
        }))
      : (changedDevices ?? treatedDevices)?.map((device: any) => ({
          iccid: device.iccid,
          processData: device.processData,
        }));

    return {
      action: ActionTypes.CHANGE_SIMCARD,
      devices: devicesPayload,
    };
  }, [isUpload, devices, changedDevices, treatedDevices]);

  return {
    steps,
    payloadToSend,
    confirmDisabled,
    modalWidth: 700,
    infoMessage: isMot && (
      <p style={{ margin: 0, fontWeight: 'bold' }}>
        {t('actions-modal:just-algar-lines')?.toUpperCase()}
      </p>
    ),
  };
};

export default useSimcardChange;
